import {
  AnalyticsEventName,
  AttendanceRecordSource,
  trackAmplitudeEvent,
} from '@schooly/analytics';
import {
  ApiError,
  AttendanceEntryForRelationUpdate,
  FilterKeys,
  Justification,
  JustificationSave,
  JustificationBase,
  JustificationsResponse,
  JustificationStatus,
  RQUseInfiniteQueryOptions,
  SchoolYear,
  useGetAttendanceCodesQuery,
  useGetAttendanceJustificationsQuery,
  useUpdateAttendanceEntriesForRelation,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import { RELATION_ID_PARAM } from '@schooly/constants';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useSchoolYears from '../../hooks/useSchoolYears';
import { getResultingJustificationStatus } from '../../pages/Attendance/attendance-utils';
import { canViewJustifications } from '../../pages/Attendance/permissions';
import { ProfileModalMode } from '../profile/helpers';

const PAGE_SIZE = 30;

type UpdateEntryCodeData = {
  relationId: string;
  registerId: string;
  studentName: string;
  entries: AttendanceEntryForRelationUpdate[];
  justification: JustificationBase;
  note?: JustificationSave;
};

export type UpdateEntryCodeParams = {
  data: UpdateEntryCodeData;
};

type ShowJustificationNotificationProps = {
  status: JustificationStatus.Closed | JustificationStatus.Pending;
  studentName: string;
  relationId: string;
};

type AttendanceJustificationsQueryOptions = {
  queryOptions: RQUseInfiniteQueryOptions<JustificationsResponse>;
};

export const useAttendanceJustifications = ({
  queryOptions,
}: AttendanceJustificationsQueryOptions) => {
  const { schoolId = '', permissions } = useAuth();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { defaultValidity } = useSchoolYears();
  const [searchParams] = useSearchParams();
  const relationId = searchParams.get(RELATION_ID_PARAM);
  const { showError, showNotification } = useNotifications();
  const { mutateAsync: updateEntriesForRelation } = useUpdateAttendanceEntriesForRelation();
  const { data: codes } = useGetAttendanceCodesQuery(schoolId);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(() => new Set());

  const canView = canViewJustifications(permissions);

  const { data, setParams, params, refetch, ...query } = useGetAttendanceJustificationsQuery(
    {
      schoolId: schoolId,
      filters: {
        statuses: [JustificationStatus.Pending],
        [FilterKeys.Date]: defaultValidity
          ? [defaultValidity?.start, defaultValidity?.end]
          : undefined,
        relationId: relationId ?? undefined,
      },
      pageSize: PAGE_SIZE,
      query: '',
    },
    { refetchOnMount: 'always', enabled: !!schoolId && canView, ...queryOptions },
  );

  const justifications = useMemo(
    () => data?.pages.reduce<Justification[]>((prev, curr) => [...prev, ...curr.results], []) ?? [],
    [data?.pages],
  );

  const invalidateListQueries = useInvalidateListQueriesFor('attendanceList', {
    refetchType: 'active',
  });

  const showJustificationNotification = useCallback(
    ({ status, studentName, relationId }: ShowJustificationNotificationProps) => {
      showNotification({
        message: formatMessage(
          {
            id:
              status === JustificationStatus.Closed
                ? 'attendance-justification-CloseSuccess'
                : 'attendance-justification-ReopenSuccess',
          },
          {
            name:
              studentName ||
              formatMessage({ id: 'attendance-justification-Student' }).toLowerCase(),
          },
        ),
        type: 'success',
        actions: studentName
          ? [
              {
                textId: 'action-ViewProfile',
                handler: () =>
                  navigate({
                    pathname: `/students/${relationId}`,
                    hash: `#${ProfileModalMode.Attendance}`,
                  }),
              },
            ]
          : undefined,
      });
    },
    [formatMessage, navigate, showNotification],
  );

  const handleUpdateEntryCode = useCallback(
    async ({ data }: UpdateEntryCodeParams) => {
      const { registerId, relationId, studentName, entries, justification, note } = data;

      setUpdatingIds((ids) => new Set(ids).add(justification.id));

      try {
        await updateEntriesForRelation({
          registerId,
          relationId,
          data: { entries, justification: note },
        });

        const newStatus = getResultingJustificationStatus({
          justification,
          note: note ?? null,
          codes: codes ?? [],
          entries,
        });

        if (newStatus === JustificationStatus.Closed || newStatus === JustificationStatus.Pending) {
          showJustificationNotification({ status: newStatus, studentName, relationId });
          trackAmplitudeEvent({
            name:
              newStatus === JustificationStatus.Closed
                ? AnalyticsEventName.AttendanceJustificationClosed
                : AnalyticsEventName.AttendanceJustificationReopen,
          });
        }

        for (const entry of entries) {
          if (!entry.session) continue;

          trackAmplitudeEvent({
            name: AnalyticsEventName.AttendanceEditRecord,
            payload: {
              source: AttendanceRecordSource.Justification,
              session: entry.session.toUpperCase(),
            },
          });
        }

        invalidateListQueries();
        await refetch();
      } catch (error) {
        showError(error as ApiError);
      } finally {
        setUpdatingIds((ids) => {
          const next = new Set(ids);
          next.delete(justification.id);
          return next;
        });
      }
    },
    [
      updateEntriesForRelation,
      codes,
      showJustificationNotification,
      invalidateListQueries,
      refetch,
      showError,
    ],
  );

  const handleQueryChange = useCallback(
    (query: string) => {
      trackAmplitudeEvent({
        name: AnalyticsEventName.AttendanceJustificationsSearch,
      });
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  const handleYearChange = useCallback(
    (year: SchoolYear) => {
      setParams((p) => ({ ...p, filters: { ...p.filters, date: [year.start, year.end] } }));
    },
    [setParams],
  );

  const handleStatusChange = useCallback(
    (status: JustificationStatus) => {
      setParams((p) => ({ ...p, filters: { ...p.filters, statuses: [status] } }));
    },
    [setParams],
  );

  return {
    data,
    justifications,
    params,
    handleQueryChange,
    handleYearChange,
    handleStatusChange,
    updatingIds,
    handleUpdateEntryCode,
    ...query,
  };
};
